import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
    Button,
} from '@zillow/constellation';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import ModalActions from 'app/shared/actions/ModalActions';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import ZillowTours from 'app/shared/components/Modal/SelectTourModal/components/ZillowTours';
import ModalFooter from 'app/shared/components/Modal/ModalFooter';
import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';


const propTypes = {
    defaultTours: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        src: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    })),
    filterOutTourIds: PropTypes.arrayOf(PropTypes.string),
    shouldLoadTours: PropTypes.bool,
    isListingPublished: PropTypes.bool,
    onLoadTours: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['floorplan', 'unit', 'amenity', 'singleFamily']),
    initialSelectedTourId: PropTypes.string,
};

const defaultProps = {
    defaultTours: [],
    filterOutTourIds: [],
    shouldLoadTours: true,
    isListingPublished: false,
    mode: 'floorplan',
    initialSelectedTourId: null,
};

const HEADER_TEXT = {
    floorplan: 'Floor plan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'Property',
};

const SF_NOT_PUBLISHED_TEXT = 'We won’t able to add this 3d tour to your listing until the listing is live on our '
    + 'sites. Once this listing is live on our sites, please try this operation again.';

const SelectTourModal = ({
    dispatch,
    filterOutTourIds,
    shouldLoadTours,
    isListingPublished,
    onLoadTours,
    onSubmit,
    onCancel,
    mode,
    defaultTours,
    initialSelectedTourId,
}) => {
    const [isLoading, setIsLoading] = React.useState(shouldLoadTours);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState();
    const [tours, setTours] = React.useState(defaultTours);
    const [selectedTourId, setSelectedTourId] = React.useState(initialSelectedTourId);

    React.useEffect(() => {
        if (shouldLoadTours) {
            onLoadTours()
                .then((loadedTours) => {
                    dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LOAD_TOURS, {
                        label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
                        value: loadedTours.length,
                    }));
                    // Remove already selected amenity tours except the initialSelectedTourId
                    const filteredTours = mode === 'amenity'
                        ? loadedTours.filter(tour => tour.id === initialSelectedTourId
                            || filterOutTourIds.indexOf(tour.id) === -1)
                        : loadedTours;
                    setTours(filteredTours);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    setErrorMessage('Cannot retrieve 3d tours, please try again later.');
                });
        }
    }, [dispatch, mode, onLoadTours, shouldLoadTours]);

    const handleSubmit = () => {
        setIsSubmitting(true);
        if (mode === 'singleFamily' && !isListingPublished) {
            setErrorMessage(SF_NOT_PUBLISHED_TEXT);
            return;
        }
        return onSubmit(selectedTourId)
            .catch(() => {
                setIsSubmitting(false);
                setErrorMessage('Cannot associate this tour, please try again later.');
            });
    };

    const handleSelectTour = (id) => {
        setErrorMessage(null);
        const isUnselecting = selectedTourId === id;
        const gaEvent = isUnselecting
            ? AnalyticsConstants.GA_EVENTS.UNSELECT_TOUR
            : AnalyticsConstants.GA_EVENTS.SELECT_TOUR;
        dispatch(analyticsUtils.track.GAEventV2(gaEvent, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
        }));
        setSelectedTourId(isUnselecting ? null : id);
    };

    const handleViewTour = (tour) => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_TOUR, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL,
        }));

        const onBackClick = () => {
            dispatch(ModalActions.showModal({
                header: `${HEADER_TEXT[mode]} tour`,
                modalType: ModalConstants.SELECT_TOUR_MODAL,
                onCancel: () => dispatch(ModalActions.hideModal()),
                onSubmit,
                isListingPublished,
                defaultTours: tours,
                mode,
                initialSelectedTourId: selectedTourId,
                shouldLoadTours: false,
                isFluid: false,
            }));
        };
        dispatch(ModalActions.showModal({
            header: tour.name,
            modalType: ModalConstants.VIEW_TOUR_MODAL,
            src: tour.src,
            title: tour.name,
            isFluid: true,
            footer: () => <Button onClick={onBackClick}>Back</Button>,
        }));
    };

    if (isLoading) {
        return <SpinnerContainer />;
    }

    return (
        <StyledMDContainer>
            <StyledMDContentContainer>
                <StyledMDContent fullWidth={true}>
                    <ZillowTours
                        onSelectTour={handleSelectTour}
                        onViewTour={handleViewTour}
                        tours={tours}
                        selectedTourId={selectedTourId}
                        mode={mode}
                    />
                </StyledMDContent>
            </StyledMDContentContainer>
            <StyledMDActionsContainer>
                <ModalFooter
                    onCancel={onCancel}
                    onSubmit={handleSubmit}
                    submitText="Save"
                    isSubmitting={isSubmitting}
                    errorMessage={errorMessage}
                    hasError={!!errorMessage}
                    isSubmitDisabled={!!errorMessage}
                />
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

SelectTourModal.propTypes = propTypes;
SelectTourModal.defaultProps = defaultProps;

export { SelectTourModal };
export default connect()(SelectTourModal);
